/**
 * Projection/addressing layer for Link topology.
 *
 * A Link does not store an ID. A projection is an external view over Link structure, useful for transport
 * indexes, persistence keys, cache negotiation, and integrity checks. It is not enough to reconstruct a Link:
 * transport layers still need the structural preimage (e.g. as [ProjectedLinkRecord]s) to reconstruct the Link.
 *
 * The projection scheme is explicit. Its protocol is represented as a parent Link, and the compact payload is
 * rendered through the codec layer. This keeps algorithm choice, preimage encoding, and payload rendering out of
 * the Link kernel, while still allowing the projection itself to be represented as ordinary Link structure. Model
 * code names projection schemes and renders their graph shape; concrete projection algorithms belong to adapters
 * or tests through [LinkProjector].
 */

package com.oasisfeng.invaluable.data

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.LinkArena
import com.oasisfeng.invaluable.LinkArena.MaterializedLink
import com.oasisfeng.invaluable.InternalTopologyApi
import com.oasisfeng.invaluable.Link
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.data.Stream.OctetStream
import com.oasisfeng.invaluable.rawTargetOrNull
import com.oasisfeng.invaluable.then
import kotlinx.io.bytestring.ByteString
import kotlin.jvm.JvmInline

/** Protocol-scoped Link projection. */
data class LinkProjection(val scheme: LinkProjectionScheme, val payload: ByteString) {
    /** Link-structural representation: `scheme.protocol -> encoded payload`. */
    fun link(): NestedLink = scheme.protocolLink.then(OctetStream.encode(payload).link)

    companion object {
        /** Link projections: Structural data collapsing into pure number. */
        internal val ANCHOR = Dimensions.DATA.into(Dimensions.QUANTITY).link
    }
}

/** Projection scheme identity. Algorithm implementation is intentionally outside the model layer. */
@JvmInline value class LinkProjectionScheme(val protocolLink: NestedLink) {
    companion object {
        fun of(protocol: NestedLink): LinkProjectionScheme = LinkProjectionScheme(LinkProjection.ANCHOR.then(protocol))
    }
}

/** Local observation result over projection-indexed records. */
sealed interface Observation<out T> {

    data class Known<T>(val value: T) : Observation<T>
    data class Missing(val projection: LinkProjection) : Observation<Nothing>
    data class Invalid(val reason: String) : Observation<Nothing>

    /** Transform a known value, treating a null conversion as invalid rather than missing. */
    fun <R : Any> mapKnownOrInvalid(invalidReason: String, transform: (T) -> R?): Observation<R> = when (this) {
        is Missing -> this
        is Invalid -> this
        is Known -> transform(value)?.let(::Known) ?: Invalid(invalidReason)
    }

    /** Combine two known observations, preserving the first missing or invalid state otherwise. */
    fun <O, R> zipKnown(other: Observation<O>, transform: (T, O) -> R): Observation<R> = when (this) {
        is Missing -> this
        is Invalid -> this
        is Known -> when (other) {
            is Missing -> other
            is Invalid -> other
            is Known -> Known(transform(value, other.value))
        }
    }
}

/** Adapter boundary for concrete projection algorithms. */
interface LinkProjector {
    val scheme: LinkProjectionScheme

    /** Projection of the global Link identified by `(parent, target)`. A null endpoint is the kernel root. */
    fun projectLink(parent: LinkProjection?, target: LinkProjection?): LinkProjection

    /**
     * Optional process-local memo of whole-Link projections, shared across [project] calls.
     *
     * [project] walks the entire structure beneath a Link, and a post body is one link per bit — so re-projecting the
     * same post costs thousands of steps every time a caller wants its ref (linkscape assembles one per node on every
     * synced advance; the board does the same per card). A [Link] is interned, so the memo keys on cheap identity and
     * the projection it holds is a pure function of that identity: a hit is the same value the walk would have
     * recomputed. Adapters that live in a long-running node supply one; toy projectors need not, and default to none.
     *
     * Deliberately *not* consulted by [projectedRecords], which must emit a record for every link it passes — there a
     * memo hit would drop structure from the wire rather than save work.
     */
    val projections: MutableMap<Link, LinkProjection>? get() = null
}

/**
 * Compute the projection of a graph-free Link.
 *
 * Iterative post-order over the Link's parent/target structure with an explicit work stack: a single post already
 * nests thousands of links deep, so resolving in heap space instead of the native call stack avoids the
 * stack-overflow that recursion hit (see [resolveLink]). The walk memoizes into [LinkProjector.projections] when the
 * projector keeps one, so every link it passes — not just the head — is free to the next caller.
 */
fun LinkProjector.project(link: Link): LinkProjection {
    val memo = projections ?: mutableMapOf()
    memo[link]?.let { return it }
    val stack = ArrayDeque<Link>()
    stack.addLast(link)
    while (stack.isNotEmpty()) {
        val current = stack.last()
        if (current in memo) { stack.removeLast(); continue }
        val parent = current.parentOrNull()
        @OptIn(InternalTopologyApi::class)
        val target = current.rawTargetOrNull()
        if (parent != null && parent !in memo) { stack.addLast(parent); continue }
        if (target != null && target !in memo) { stack.addLast(target); continue }
        memo[current] = projectLink(parent?.let(memo::getValue), target?.let(memo::getValue))
        stack.removeLast()
    }
    return memo.getValue(link)
}

/** Convenience boundary for projectors that operate on a materialized local Link. */
fun LinkProjector.project(link: MaterializedLink): LinkProjection = project(link.link)

/**
 * Projection-indexed Link preimage.
 *
 * Unlike local index-based preimages, this record is not closure-local. It names each endpoint by global projection,
 * so a store can retain a Link whose parent or target preimage is not visible yet. Null parent/target endpoints are
 * the globally known kernel root, not missing preimages. That is the projection-observation boundary:
 * the Link is globally named, but local materialization may have to stop at a missing non-root preimage.
 */
data class ProjectedLinkRecord(val projection: LinkProjection, val parent: LinkProjection?, val target: LinkProjection?) {
    init {
        require(parent == null || projection.scheme == parent.scheme)
        { "Link record scheme must match parent scheme." }
        require(target == null || projection.scheme == target.scheme)
        { "Link record scheme must match target scheme." }
    }
}

/** Resolver for projection-indexed Link records visible to the local node. */
fun interface ProjectedLinkRecordResolver {
    fun recordFor(projection: LinkProjection): ProjectedLinkRecord?
}

/**
 * A resolver that carries a memo of the whole Links [resolveLink] has already rebuilt through it.
 *
 * [resolveLink] rebuilds a Link by walking its entire projection DAG, and that DAG is deeply shared: an actor's
 * commitment spines chain through one another, so resolving each of a log's heads in turn re-walks the same ancestry
 * every time — quadratic in the log — and one [observeSchema] resolves four overlapping projections of the same item.
 * The memo makes that walk once per pass instead of once per call.
 *
 * **Scope it to one read pass and throw it away.** A memo entry says "this projection resolved here", which stays true
 * only while the records behind it stay visible: content can be forgotten locally ([ReplicatedEventStore.removePreimage]),
 * and a resolver decorated with capabilities sees material a bare store resolver must not. Both are honest state changes
 * a stale memo would hide. Nothing here expires, so a long-lived instance is a bug, not a slow cache.
 *
 * [inherited] lets a short pass read what a longer-lived one already resolved without writing back into it — see
 * [memoizingOver] for when that is sound.
 */
class MemoizingLinkRecordResolver internal constructor(
    private val records: ProjectedLinkRecordResolver,
    /** Resolutions from an enclosing pass: readable here, never written to. Empty for a self-contained memo. */
    internal val inherited: Map<LinkProjection, Link> = emptyMap(),
) : ProjectedLinkRecordResolver {
    internal val links = HashMap<LinkProjection, Link>()
    override fun recordFor(projection: LinkProjection): ProjectedLinkRecord? = records.recordFor(projection)

    /** What this pass has resolved so far — the product a caller can hand to another pass via [adopt]. */
    val resolutions: Map<LinkProjection, Link> get() = links

    /**
     * Take [resolutions] from a shorter pass as this pass's own.
     *
     * The counterpart to [memoizingOver]'s one-way read: an overlay pass may not write back *while* its records are
     * merely proposed, but once the caller has made those records durable here the reason for the asymmetry is gone and
     * the resolutions are as true in this pass as in the one that made them. Only the caller knows that moment, which is
     * why this is an explicit act and not a side effect of resolving.
     *
     * Adopting resolutions whose records did **not** become visible here would make this memo assert what the store
     * cannot show — the same staleness [MemoizingLinkRecordResolver] warns about, arrived at from the other direction.
     */
    fun adopt(resolutions: Map<LinkProjection, Link>) { links.putAll(resolutions) }
}

/**
 * Wrap this resolver so every [resolveLink] against the result shares one resolution memo — for the duration of one
 * read pass (see [MemoizingLinkRecordResolver]).
 *
 * Idempotent: wrapping an already-memoizing resolver returns it unchanged, so an inner pass (one schema observation)
 * automatically joins the outer pass's memo (a whole view build) rather than starting its own.
 */
fun ProjectedLinkRecordResolver.memoizing(): MemoizingLinkRecordResolver =
    this as? MemoizingLinkRecordResolver ?: MemoizingLinkRecordResolver(this)

/**
 * Memoize this resolver as its own pass, but let it *read* what [outer]'s pass has already resolved.
 *
 * For a pass that sees strictly more than [outer] does — an overlay resolving records [outer] holds plus records it
 * merely proposes — a plain [memoizing] starts empty and rebuilds the shared ancestry from scratch. Since a Link's
 * projection is content-addressed, a projection [outer] resolved can only resolve the same way here, so reading its
 * memo is free and sound; writing back is not, because this pass's own resolutions may rest on proposed records that
 * are about to be discarded. Hence one-way: reads fall through, writes stay local and die with this instance.
 *
 * [outer]'s memo is read live, so entries it gains later are visible too. That makes this unsound if [outer] can
 * *narrow* while this pass is alive (a local erasure — see [MemoizingLinkRecordResolver]); confine it to a pass short
 * enough that it cannot, as a single batch validation is.
 */
fun ProjectedLinkRecordResolver.memoizingOver(outer: ProjectedLinkRecordResolver): MemoizingLinkRecordResolver =
    MemoizingLinkRecordResolver(this, (outer as? MemoizingLinkRecordResolver)?.links ?: emptyMap())

/**
 * Extract all projected Link records for a Link.
 *
 * Iterative post-order (see [project]) so a deep Link does not overflow the native call stack. Records are emitted
 * children-before-parent, the same order the previous recursive post-order produced.
 */
fun LinkProjector.projectedRecords(link: Link): List<ProjectedLinkRecord> = projectedRecords(link, HashMap())

/**
 * [projectedRecords] against a walk memo the *caller* owns: emits only what [walked] does not already account for, and
 * grows [walked] by exactly those.
 *
 * A caller that projects a growing chain — each head built on the last, as an actor's commitment log is — otherwise
 * re-walks the entire closure beneath every head, because a commitment's Link *is* its whole ancestry. That is O(n²)
 * over the chain even though each head adds a constant amount: measured on a real relay, the k-th backing walked a
 * closure of length k while producing about 255 new records regardless of k. Carrying one memo across the calls makes
 * each walk cost what that head actually added.
 *
 * Sound because [Link] is globally interned (`LinkArena`), so a node reached twice is the *same object* and a memo hit
 * means genuinely-already-walked, not merely structurally-similar. What the caller must supply is the other half: the
 * records suppressed here are ones it has already dealt with, since this cannot know what it did with them. A memo
 * shared across unrelated purposes silently drops records the second purpose never saw.
 */
fun LinkProjector.projectedRecords(link: Link, walked: MutableMap<Link, LinkProjection>): List<ProjectedLinkRecord> {
    val records = mutableListOf<ProjectedLinkRecord>()
    val memo = walked
    val stack = ArrayDeque<Link>()
    stack.addLast(link)
    while (stack.isNotEmpty()) {
        val current = stack.last()
        if (current in memo) { stack.removeLast(); continue }
        val parent = current.parentOrNull()
        @OptIn(InternalTopologyApi::class)
        val target = current.rawTargetOrNull()
        if (parent != null && parent !in memo) { stack.addLast(parent); continue }
        if (target != null && target !in memo) { stack.addLast(target); continue }
        val parentProjection = parent?.let(memo::getValue)
        val targetProjection = target?.let(memo::getValue)
        val projection = projectLink(parentProjection, targetProjection)
        records += ProjectedLinkRecord(projection, parentProjection, targetProjection)
        memo[current] = projection
        stack.removeLast()
    }
    return records
}

/** Convenience boundary for extracting projected records from a materialized local Link. */
fun LinkProjector.projectedRecords(link: MaterializedLink): List<ProjectedLinkRecord> = projectedRecords(link.link)

fun LinkProjector.materializeResolved(projection: LinkProjection, arena: LinkArena, resolver: ProjectedLinkRecordResolver): Observation<MaterializedLink>
= when (val link = resolveLink(projection, resolver)) {
    is Observation.Known -> Observation.Known(arena.materialize(link.value))
    is Observation.Missing -> link
    is Observation.Invalid -> link
}

fun LinkProjector.resolveLink(projection: LinkProjection, resolver: ProjectedLinkRecordResolver): Observation<Link> {
    requireProjection(projection, "Head")

    val visiting = mutableSetOf<LinkProjection>()
    // Shared with the rest of this read pass when the caller asked for it (see [memoizing]); private otherwise.
    val memo = resolver as? MemoizingLinkRecordResolver
    val resolved = memo?.links ?: mutableMapOf()
    // Read-only resolutions from an enclosing pass, if this one was opened over it (see [memoizingOver]).
    val inherited = memo?.inherited ?: emptyMap()

    // Iterative depth-first resolution with an explicit frame stack. The previous version recursed once per link in
    // the parent/target chain; a single post nests thousands deep, so that overflowed the native call stack — a
    // non-deterministic crash of every context read and of node startup. Heap frames remove the depth ceiling while
    // preserving the original semantics: memoize resolved links, detect cycles along the active path, resolve parent
    // before target, and short-circuit on the first Missing/Invalid.
    class Frame(val projection: LinkProjection) {
        var phase = 0                            // 0 enter; 1 parent resolved (resolve target); 2 target resolved (combine)
        var record: ProjectedLinkRecord? = null
        var parentLink: Link? = null
    }

    val stack = ArrayDeque<Frame>()
    stack.addLast(Frame(projection))
    var result: Observation<Link> = Observation.Missing(projection)   // the last completed frame's result

    // Pop a frame that entered `visiting`, carrying `observation` up to its parent on the next iteration.
    fun complete(observation: Observation<Link>) {
        val done = stack.removeLast()
        visiting.remove(done.projection)
        if (observation is Observation.Known) resolved[done.projection] = observation.value
        result = observation
    }

    while (stack.isNotEmpty()) {
        val frame = stack.last()
        val current = frame.projection
        when (frame.phase) {
            0 -> {
                requireProjection(current, "Record")
                val cached = resolved[current] ?: inherited[current]
                if (cached != null) {                                  // memo hit: never entered `visiting`
                    stack.removeLast(); result = Observation.Known(cached); continue
                }
                val record = resolver.recordFor(current)
                if (record == null) {                                  // body not visible locally
                    stack.removeLast(); result = Observation.Missing(current); continue
                }
                if (! visiting.add(current)) {                         // back-edge to a frame still on the active path
                    stack.removeLast(); result = Observation.Invalid("Cycle in projected Link records."); continue
                }
                // `current` is now on the active path; every exit below goes through complete() to leave `visiting`.
                if (record.projection != current) {
                    complete(Observation.Invalid("Projected Link record returned for the wrong projection.")); continue
                }
                record.parent?.let { requireProjection(it, "Parent") }
                record.target?.let { requireProjection(it, "Target") }
                if (projectLink(record.parent, record.target) != current) {
                    complete(Observation.Invalid("Projected Link record projection does not match its endpoints.")); continue
                }
                frame.record = record
                frame.phase = 1
                record.parent?.let { stack.addLast(Frame(it)) }        // resolve parent first (or fall straight to phase 1)
            }
            1 -> {
                val record = frame.record!!
                when (val parent = if (record.parent == null) null else result) {
                    is Observation.Missing -> complete(parent)
                    is Observation.Invalid -> complete(parent)
                    is Observation.Known, null -> {
                        frame.parentLink = parent?.value
                        frame.phase = 2
                        record.target?.let { stack.addLast(Frame(it)) }
                    }
                }
            }
            else -> {
                val record = frame.record!!
                when (val target = if (record.target == null) null else result) {
                    is Observation.Missing -> complete(target)
                    is Observation.Invalid -> complete(target)
                    is Observation.Known, null -> complete(Observation.Known(frame.parentLink.then(target?.value)))
                }
            }
        }
    }

    return result
}

private fun LinkProjector.requireProjection(projection: LinkProjection, role: String) {
    require(projection.scheme == scheme) { "$role projection scheme does not match projector scheme." }
}

/** Try to render a projection Link back to its protocol-scoped compact form. */
fun renderProjection(link: NestedLink): LinkProjection? {
    val schemeProtocol = link.parent().nestedOrNull() ?: return null
    if (! schemeProtocol.isChildOf(LinkProjection.ANCHOR)) return null
    val payload = link.nestedTargetOrNull()?.let(OctetStream::render) ?: return null
    return LinkProjection(LinkProjectionScheme(schemeProtocol), payload)
}
